import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tournament, TournamentType, CricketFormat } from '../types';
import apiService from '../services/api';

interface TournamentListProps {
  tournaments?: any[];
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  isAdmin?: boolean;
  onJoinLeague?: (league: any) => void; // New prop for handling league joins
  showJoinButton?: boolean; // Whether to show join button
}

const TournamentList: React.FC<TournamentListProps> = ({
  tournaments: propTournaments = [],
  loading = false,
  error = null,
  onRefresh,
  isAdmin = false,
  onJoinLeague,
  showJoinButton = true
}) => {
  console.log('TournamentList tournaments prop:', propTournaments);
  const safeTournaments = Array.isArray(propTournaments) ? propTournaments : [];
  const navigate = useNavigate();
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<TournamentType | 'all'>('all');
  const [selectedFormat, setSelectedFormat] = useState<CricketFormat | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'upcoming' | 'active' | 'completed'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'start_date' | 'participants'>('start_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  // Filter and sort tournaments
  const filteredAndSortedTournaments = useMemo(() => {
    let filtered = safeTournaments.filter(tournament => {
      const matchesSearch = tournament.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = selectedType === 'all' || tournament.type === selectedType;
      const matchesFormat = selectedFormat === 'all' || tournament.format === selectedFormat;
      const matchesStatus = selectedStatus === 'all' || tournament.status === selectedStatus;
      return matchesSearch && matchesType && matchesFormat && matchesStatus;
    });
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'start_date':
          aValue = new Date(a.start_date);
          bValue = new Date(b.start_date);
          break;
        case 'participants':
          aValue = a.current_participants;
          bValue = b.current_participants;
          break;
        default:
          return 0;
      }
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    return filtered;
  }, [safeTournaments, searchTerm, selectedType, selectedFormat, selectedStatus, sortBy, sortOrder]);

  const getTournamentTypeInfo = (type: TournamentType) => {
    switch (type) {
      case 'pickem':
        return { label: "Pick'em", icon: '\ud83c\udfaf', color: 'bg-secondary text-primary' };
      case 'budget':
        return { label: 'Budget League', icon: '\ud83d\udcb0', color: 'bg-accent text-primary' };
      case 'draft':
        return { label: 'Draft League', icon: '\ud83d\udccb', color: 'bg-primary text-accent' };
      case 'h2h':
        return { label: 'H2H Matches', icon: '\u2694\ufe0f', color: 'bg-primary text-secondary' };
      case 'eliminator':
        return { label: 'Eliminator', icon: '\ud83d\udc80', color: 'bg-accent text-primary' };
      default:
        return { label: type, icon: '\ud83c\udfcf', color: 'bg-gray-500 text-white' };
    }
  };

  const getFormatInfo = (format: CricketFormat) => {
    switch (format) {
      case 'test':
        return { label: 'Test', color: 'bg-red-100 text-red-800' };
      case 'odi':
        return { label: 'ODI', color: 'bg-blue-100 text-blue-800' };
      case 't20':
        return { label: 'T20', color: 'bg-green-100 text-green-800' };
      case 't10':
        return { label: 'T10', color: 'bg-purple-100 text-purple-800' };
      default:
        return { label: format, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'active':
        return { label: 'Active', color: 'bg-green-100 text-green-800' };
      case 'upcoming':
        return { label: 'Upcoming', color: 'bg-blue-100 text-blue-800' };
      case 'completed':
        return { label: 'Completed', color: 'bg-gray-100 text-gray-800' };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'TBD';
    // Extract just the date part if it includes time
    const dateOnly = dateString.split('T')[0];
    const date = new Date(dateOnly);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewDetails = (item: any) => {
    // Check if this is a league (has league_type, tournament_type, or type)
    const leagueType = item.league_type || item.tournament_type || item.type;
    
    if (leagueType) {
      // It's a league - navigate to the appropriate league detail page
      const leagueId = item.id;
      if (leagueType === 'pickem') {
        navigate(`/pickem-league/${leagueId}`);
      } else if (leagueType === 'eliminator') {
        navigate(`/eliminator-league/${leagueId}`);
      } else if (leagueType === 'budget_league') {
        navigate(`/budget-league/${leagueId}`);
      } else if (leagueType === 'draft_league' || leagueType === 'draft') {
        navigate(`/draft-league/${leagueId}`);
      } else if (leagueType === 'h2h') {
        navigate(`/h2h-league/${leagueId}`);
      } else {
        // For other league types, show a message
        alert(`Individual league pages for ${leagueType} leagues are not available yet.`);
      }
    } else {
      // It's a tournament - navigate to tournament detail page
      navigate(`/tournament/${item.id}`);
    }
  };

  const handleJoinTournament = async (tournament: any) => {
    if (onJoinLeague) {
      // Use the custom join handler if provided
      onJoinLeague(tournament);
    } else {
      // Fallback to the old behavior for backward compatibility
      alert('Join tournament functionality coming soon!');
    }
  };

  const handleToggleVisibility = async (tournament: any) => {
    setUpdatingId(tournament.id);
    try {
      await apiService.updateTournamentAdmin(tournament.id, { is_visible: !tournament.is_visible });
      if (onRefresh) onRefresh();
    } catch (err) {
      alert('Failed to update visibility');
    } finally {
      setUpdatingId(null);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 text-xl mb-2">⚠️</div>
        <p className="text-white mb-4">{error}</p>
        {onRefresh && (
          <button 
            onClick={onRefresh}
            className="bg-accent hover:bg-secondary text-primary font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-primary min-h-screen p-4">
      {/* Search and Filters */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20">
        {/* Desktop filters */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-white mb-2">Search Tournaments</label>
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {/* Tournament Type Filter */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as TournamentType | 'all')}
              className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all" className="text-gray-800">All Types</option>
              <option value="pickem" className="text-gray-800">Pick'em</option>
              <option value="budget" className="text-gray-800">Budget League</option>
              <option value="draft" className="text-gray-800">Draft League</option>
              <option value="h2h" className="text-gray-800">H2H Matches</option>
              <option value="eliminator" className="text-gray-800">Eliminator</option>
            </select>
          </div>
          {/* Format Filter */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Format</label>
            <select
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value as CricketFormat | 'all')}
              className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all" className="text-gray-800">All Formats</option>
              <option value="test" className="text-gray-800">Test</option>
              <option value="odi" className="text-gray-800">ODI</option>
              <option value="t20" className="text-gray-800">T20</option>
              <option value="t10" className="text-gray-800">T10</option>
            </select>
          </div>
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as 'all' | 'upcoming' | 'active' | 'completed')}
              className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all" className="text-gray-800">All Status</option>
              <option value="upcoming" className="text-gray-800">Upcoming</option>
              <option value="active" className="text-gray-800">Active</option>
              <option value="completed" className="text-gray-800">Completed</option>
            </select>
          </div>
          {/* Sort */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Sort By</label>
            <div className="flex">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="flex-1 px-3 py-2 bg-white/20 border border-white/30 rounded-l-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="start_date" className="text-gray-800">Start Date</option>
                <option value="name" className="text-gray-800">Name</option>
                <option value="participants" className="text-gray-800">Participants</option>
              </select>
              <button
                onClick={toggleSortOrder}
                className="px-3 py-2 bg-white/20 border border-l-0 border-white/30 rounded-r-lg text-white hover:bg-white/30 transition-colors"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile filters */}
        <div className="md:hidden space-y-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full flex items-center justify-between bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
          >
            <span>Filters</span>
            <span className="text-sm text-white/70">{showFilters ? 'Hide' : 'Show'}</span>
          </button>
          {showFilters && (
            <div className="space-y-3 bg-white/5 border border-white/10 rounded-lg p-3">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Search Tournaments</label>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">Type</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value as TournamentType | 'all')}
                  className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all" className="text-gray-800">All Types</option>
                  <option value="pickem" className="text-gray-800">Pick'em</option>
                  <option value="budget" className="text-gray-800">Budget League</option>
                  <option value="draft" className="text-gray-800">Draft League</option>
                  <option value="h2h" className="text-gray-800">H2H Matches</option>
                  <option value="eliminator" className="text-gray-800">Eliminator</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">Format</label>
                <select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value as CricketFormat | 'all')}
                  className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all" className="text-gray-800">All Formats</option>
                  <option value="test" className="text-gray-800">Test</option>
                  <option value="odi" className="text-gray-800">ODI</option>
                  <option value="t20" className="text-gray-800">T20</option>
                  <option value="t10" className="text-gray-800">T10</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as 'all' | 'upcoming' | 'active' | 'completed')}
                  className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all" className="text-gray-800">All Status</option>
                  <option value="upcoming" className="text-gray-800">Upcoming</option>
                  <option value="active" className="text-gray-800">Active</option>
                  <option value="completed" className="text-gray-800">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">Sort By</label>
                <div className="flex">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="flex-1 px-3 py-2 bg-white/20 border border-white/30 rounded-l-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="start_date" className="text-gray-800">Start Date</option>
                    <option value="name" className="text-gray-800">Name</option>
                    <option value="participants" className="text-gray-800">Participants</option>
                  </select>
                  <button
                    onClick={toggleSortOrder}
                    className="px-3 py-2 bg-white/20 border border-l-0 border-white/30 rounded-r-lg text-white hover:bg-white/30 transition-colors"
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Results Count */}
      <div className="flex justify-between items-center">
        <p className="text-white/80">
          Showing {filteredAndSortedTournaments.length} of {propTournaments.length} leagues
        </p>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            Refresh
          </button>
        )}
      </div>
      {/* Tournament List */}
      {filteredAndSortedTournaments.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🏏</div>
          <p className="text-xl text-white mb-2">No public leagues created for this tournament</p>
          <p className="text-blue-200">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAndSortedTournaments.map((tournament) => {
            const typeInfo = getTournamentTypeInfo(tournament.type);
            const formatInfo = getFormatInfo(tournament.format);
            const statusInfo = getStatusInfo(tournament.status);
            return (
              <div key={tournament.id} className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 hover:bg-white/15 transition-all duration-200">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  {/* Left side - Tournament info */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="text-2xl">{typeInfo.icon}</span>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{tournament.name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color} text-white`}>
                            {typeInfo.label}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${formatInfo.color}`}>
                            {formatInfo.label}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-white/60">Participants:</span>
                        <div className="text-white font-medium">{tournament.current_participants}/{tournament.max_participants}</div>
                      </div>
                      <div>
                        <span className="text-white/60">Start Date:</span>
                        <div className="text-white font-medium">{formatDate(tournament.start_date)}</div>
                      </div>
                      <div className="col-span-2 md:col-span-1">
                        <span className="text-white/60">Progress:</span>
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-white/20 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(tournament.current_participants / tournament.max_participants) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-white/60">
                            {Math.round((tournament.current_participants / tournament.max_participants) * 100)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side - Action buttons */}
                  <div className="flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-2">
                    <button
                      onClick={() => handleViewDetails(tournament)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      View Details
                    </button>
                    {showJoinButton && (
                      <button
                        onClick={() => handleJoinTournament(tournament)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        disabled={tournament.status === 'completed' || tournament.current_participants >= tournament.max_participants}
                      >
                        {tournament.status === 'completed' ? 'Completed' : 
                         tournament.current_participants >= tournament.max_participants ? 'Full' : 'Join'}
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => handleToggleVisibility(tournament)}
                        className={`px-3 py-1 rounded ${tournament.is_visible ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white font-semibold transition-colors text-sm`}
                        disabled={updatingId === tournament.id}
                      >
                        {updatingId === tournament.id ? 'Updating...' : tournament.is_visible ? 'Hide' : 'Show'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TournamentList;